/**
 * A `returnUrl` is attacker-influenced input. `sessionGuard` writes the
 * genuinely attempted URL, but the sign-in page reads the value back out of
 * the query string, so `/sign-in?returnUrl=<anything>` reaches the
 * post-sign-in navigation.
 *
 * Only a same-origin absolute path is allowed through. The protocol-relative
 * shapes (`//host`, and the `/\host` variant browsers resolve the same way)
 * are rejected so that staying on-origin is an invariant of the value itself
 * rather than a property of however the router happens to serialize it — the
 * check keeps holding across router upgrades. Whitespace and control
 * characters are rejected because URL parsing strips some of them, which can
 * change a value's meaning after it has already been validated.
 *
 * Returns the path when it is safe to navigate to, otherwise null so the
 * caller falls back to the role's authorized home.
 */
export function safeReturnUrl(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || value === '') return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001f\u007f]/.test(value)) return null;
  return value;
}
